<!--
  Rechtliches → Technische Sicherheitseinrichtung (#613/#631, reworked).

  Structure, top to bottom: the one-time PUK (a secret that is shown exactly once, so nothing may sit above
  it), then the facts — „Übersicht" with its tiles — then, for an owner, the lifecycle under „Optionen", and
  last the history. Facts before actions, because every action here retires or re-mints a legal signing
  device.

  Every German label the end-user documentation quotes is unchanged („Übersicht", „Informationen", „ID",
  „Seriennummer", „Beschreibung", „Anbieter-Status", „Zeiten", „Erstellt", „Vorbereitet", „Initialisiert",
  „Defekt", „Deaktiviert", „Geräte", „Aktive Transaktionen", „Metadata", „Signatur-Algorithmus",
  „Optionen", „Neue TSE", „Frühere TSE" and the PUK sentences). What changed is markup and hierarchy:
  the tile titles were `<a>` elements with no `href` and are now headings; the label/value pairs were
  Bootstrap `fw-bold`/`fw-light` classes, which resolve to NOTHING in this app's Tailwind build (so the
  labels were never actually bold) and are now `<dl>` pairs; ids and serial numbers were `truncate`d, which
  cut the one string on the screen a reader has to transcribe, and now wrap.

  #769 — the page is now also read by a `Steuerberater`, the company's own tax adviser, and three things
  followed from walking it as one rather than as an administrator:

  * „BSI-Zertifizierungs-ID", „Signaturzähler" and „Transaktionszähler" are NEW. All three were already on
    the wire and none was ever rendered. They are the facts an adviser actually came for: the certificate
    under which the TSE signs (§ 146a AO / BSI TR-03153, and the `BSI_ZERTIFIZIERUNGS_ID` column of every
    DSFinV-K export), and the two monotonic counters whose continuity is the evidence that no signature is
    missing. „Geräte" and „Aktive Transaktionen" are capacity, not continuity — they are not these.
  * „Zertifizierung gültig bis" (#769 follow-up) completes the first of those. The field
    (`bsi_certification_valid_to`) was on the wire too and — unlike the three above — was never even mapped
    into the core model, so the page could not have rendered it. An id on its own cannot answer the question
    an adviser is actually asking of it: did that certification still stand on the trading day the export
    covers? The `@if` beside the row carries the two decisions it encodes — unanswered ⇒ silent, past ⇒ said
    plainly and no further.
  * „Beschreibung" is now OWNER-ONLY. It is provider-authored free text, and what the provider actually
    writes there is its own product name — which is the one thing Feature #241 forbids on a screen a
    customer or their adviser sees. The TSE's identity here is its „ID" and „Seriennummer"; what it IS, is
    said by „BSI-Zertifizierungs-ID" and „Signatur-Algorithmus". Support keeps the raw string in owner mode,
    where naming the supplier is not only allowed but the point.
  * „Anbieter-Status" is rendered ONCE, in the badge row. It used to appear a second time, identically, in
    the „Informationen" tile. Its VALUE was audited against the same rule and is clean — see
    `TechnicalSecuritySystemModel.providerState`.
-->
<div class="space-y-4">
  @if (!isOwner()) {
    <app-legal-readonly-hint/>
  }

  @if (loadError(); as err) {
    <div class="rounded-2xl border-[1px] border-red-100 bg-red-50 p-4 text-sm text-red-700" role="alert">
      <p class="font-semibold">{{ err.message }}</p>
      @if (err.code) {
        <p class="mt-1 break-all font-mono text-xs text-red-700">{{ err.code }}</p>
      }
      <div class="mt-3 flex flex-wrap gap-3">
        <button (click)="load()" type="button"
                class="inline-flex items-center justify-center rounded-2xl border-[1px] border-red-200
                       bg-white px-4 py-2.5 text-sm font-semibold text-red-700 shadow-sm transition-all
                       duration-200 hover:bg-red-100 active:scale-95">
          Erneut laden
        </button>
        @if (isOwner()) {
          <button (click)="openReinitializeDialog()" type="button"
                  class="inline-flex items-center justify-center rounded-2xl bg-theme-primary-700 px-4
                         py-2.5 text-sm font-semibold text-white shadow-sm transition-all duration-200
                         hover:bg-theme-primary-800 active:scale-95">
            Neue TSE anlegen
          </button>
        }
      </div>
    </div>
  }

  @if (reinitializeError(); as err) {
    <div class="rounded-2xl border-[1px] border-red-100 bg-red-50 p-4 text-sm text-red-700" role="alert">
      <p class="font-semibold">{{ err.message }}</p>
      @if (err.step) {
        <p class="mt-1">Letzter Schritt: {{ LegalTssReinitializeStepHelper.toDisplayString(err.step) }}</p>
      }
      @if (err.code) {
        <p class="mt-1 break-all font-mono text-xs text-red-700">{{ err.code }}</p>
      }
      @if (err.resumable && isOwner()) {
        <button (click)="resumeReinitialize()" [disabled]="reinitializing()" type="button"
                class="mt-3 inline-flex items-center justify-center rounded-2xl bg-theme-primary-700 px-4
                       py-2.5 text-sm font-semibold text-white shadow-sm transition-all duration-200
                       hover:bg-theme-primary-800 active:scale-95 disabled:cursor-not-allowed
                       disabled:opacity-30">
          Fortsetzen
        </button>
      }
    </div>
  }

  @if (current(); as current) {
    @if (current.adminPuk != '') {
      <!-- Der PUK. Ganz oben, hervorgehoben, und genau einmal sichtbar. -->
      <div class="rounded-2xl border-2 border-theme-primary-700 bg-white p-4 shadow-lg
                  shadow-theme-primary-100"
           data-testid="tss-puk" role="alert">
        <div class="flex items-start gap-3">
          <span aria-hidden="true"
                class="inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl
                       bg-theme-primary-700 text-white">
            <svg class="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
              <path
                d="M4 4a2 2 0 0 1 2-2h6.172a2 2 0 0 1 1.414.586l5.11 5.109a3 3 0 0 0-.302.267L16.356 10H14a2 2 0 0 1-2-2V3.5H6a.5.5 0 0 0-.5.5v16a.5.5 0 0 0 .5.5h12a.5.5 0 0 0 .5-.5v-2.85l1.5-1.5V20a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2zm10 4.5h3.38L13.5 4.62V8a.5.5 0 0 0 .5.5m-.803 6.072L19.1 8.669a2.286 2.286 0 1 1 3.232 3.233l-5.902 5.902a2.7 2.7 0 0 1-1.248.707l-1.83.457A1.1 1.1 0 0 1 13 19H7.75a.75.75 0 0 1 0-1.5h4.321l.42-1.68c.118-.473.362-.904.706-1.248"/>
            </svg>
          </span>
          <div class="min-w-0 flex-1">
            <p class="text-base font-bold tracking-tight text-theme-primary-700">
              WICHTIG - PUK wird nur einmal angezeigt, notieren Sie diesen an einer sicheren Stelle.
            </p>
            <button (click)="copyInformation(current.adminPuk)" aria-label="PUK in die Zwischenablage kopieren"
                    data-testid="tss-puk-copy" type="button"
                    class="mt-2 block w-full break-all rounded-xl border-[1px] border-gray-200 bg-gray-50
                           px-3 py-2 text-left font-mono text-xl font-bold text-gray-900 transition-colors
                           duration-200 hover:bg-gray-100 focus-visible:outline focus-visible:outline-2
                           focus-visible:outline-offset-2 focus-visible:outline-theme-primary-500">
              {{ current.adminPuk }}
            </button>
            <p class="mt-2 text-xs text-gray-500">PUK anklicken zum Kopieren.</p>
          </div>
        </div>
      </div>
    }

    <!-- ── Übersicht ──────────────────────────────────────────────────────────────────────────────────── -->
    <section aria-labelledby="tss-overview-title"
             class="rounded-2xl border-[1px] border-gray-100 bg-white p-4 shadow-sm">
      <div class="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <h2 class="text-base font-bold tracking-tight text-gray-800" id="tss-overview-title">Übersicht</h2>
        <div class="flex flex-wrap items-center gap-2">
          <!-- The app's own four-value lifecycle. green-700/green-50 = 5.01:1, amber-700/amber-50 = 4.85:1,
               red-700/red-50 = 5.91:1, gray-600/gray-100 = 6.87:1 — the -600 shades all fail AA here. -->
          <span data-testid="tss-state"
                class="inline-flex items-center rounded-full px-2 py-0.5 align-top text-xs font-semibold"
                [class.bg-green-50]="current.state === KassenSichVState.Initialized"
                [class.text-green-700]="current.state === KassenSichVState.Initialized"
                [class.bg-amber-50]="current.state === KassenSichVState.Created"
                [class.text-amber-700]="current.state === KassenSichVState.Created"
                [class.bg-red-50]="current.state === KassenSichVState.Disabled"
                [class.text-red-700]="current.state === KassenSichVState.Disabled"
                [class.bg-gray-100]="current.state === KassenSichVState.New"
                [class.text-gray-600]="current.state === KassenSichVState.New">
            {{ KassenSichVStateHelper.toDisplayString(current.state) }}
          </span>
          @if (current.providerState) {
            <span class="inline-flex items-center gap-1 text-xs text-gray-500">
              Anbieter-Status:
              <span class="font-mono text-xs text-gray-600">{{ current.providerState }}</span>
            </span>
          }
        </div>
      </div>

      <!-- #910: the TSE's current id belongs to a different fiskaly organisation than the stored key — the
           typical aftermath of a TEST→LIVE credential switch. `state` already reads `Disabled` (never the old
           virtual „Neu"), which is what already puts „Neue TSE" under „Optionen" below; this notice is the
           readable explanation `providerState` alone does not give. `warning`, not `error`: `GetCurrent`
           answered 200, nothing failed — this is a standing fact about the TSE, not a refusal. -->
      @if (current.providerState === FOREIGN_ORGANISATION_PROVIDER_STATE) {
        <cqme-inline-notice class="mt-3" tone="warning" data-testid="tss-foreign-organisation-notice">
          Diese TSE gehört nicht zum hinterlegten fiskaly-Zugang. Legen Sie eine neue TSE an.
        </cqme-inline-notice>
      }

      <ul class="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3" role="list">
        <!-- Informationen — die Identität der TSE. Über zwei Spalten, weil ID und Seriennummer lang sind. -->
        <li class="rounded-xl border-[1px] border-gray-100 bg-gray-50/50 p-3 sm:col-span-2">
          <div class="flex items-center gap-2">
            <span aria-hidden="true"
                  class="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-white
                         text-theme-primary-600 shadow-sm">
              <svg class="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                <path
                  d="M12 2c5.523 0 10 4.477 10 10s-4.477 10-10 10S2 17.523 2 12S6.477 2 12 2m-.01 8H11a1 1 0 0 0-.117 1.993L11 12v4.99c0 .52.394.95.9 1.004l.11.006h.49a1 1 0 0 0 .596-1.803L13 16.134V11.01c0-.52-.394-.95-.9-1.004zM12 7a1 1 0 1 0 0 2a1 1 0 0 0 0-2"/>
              </svg>
            </span>
            <h3 class="text-sm font-bold tracking-tight text-gray-800">Informationen</h3>
          </div>
          <dl class="mt-2 space-y-1 text-sm">
            <div class="flex flex-wrap items-baseline gap-x-2">
              <dt class="font-semibold text-gray-700">ID:</dt>
              <dd class="min-w-0 break-all font-mono text-xs text-gray-500">{{ current.id }}</dd>
            </div>
            <div class="flex flex-wrap items-baseline gap-x-2">
              <dt class="font-semibold text-gray-700">Seriennummer:</dt>
              <dd class="min-w-0 break-all font-mono text-xs text-gray-500">{{ current.serialNumber }}</dd>
            </div>
            <!-- Owner-only: provider-authored free text, whose production content is the supplier's own
                 product name. See the file header. -->
            @if (isOwner()) {
              <div class="flex flex-wrap items-baseline gap-x-2">
                <dt class="font-semibold text-gray-700">Beschreibung:</dt>
                <dd class="min-w-0 text-gray-500">{{ current.description }}</dd>
              </div>
            }
            @if (current.bsiCertificationId) {
              <div class="flex flex-wrap items-baseline gap-x-2">
                <dt class="font-semibold text-gray-700">BSI-Zertifizierungs-ID:</dt>
                <dd class="min-w-0 break-all font-mono text-xs text-gray-500"
                    data-testid="tss-bsi-certification-id">{{ current.bsiCertificationId }}</dd>
              </div>
            }
            <!-- The other half of the certification (#769 follow-up). Rendered ONLY when the provider answered
                 an expiry: a `0` is "not answered", and printing `01.01.1970` or „unbefristet" would both be
                 the screen answering a question the provider did not. A date already in the past keeps its
                 exact value and gains „abgelaufen" beside it — the reader should not have to date-subtract in
                 their head, and for a tax adviser this is the one line on the tile that can invalidate an
                 export's certification column. It stops at that word on purpose: amber, inside the tile, no
                 banner and no state change, because whether the TSE still SIGNS is what the badge and
                 „Anbieter-Status" above say, and this must not contradict a provider reporting INITIALIZED. -->
            @if (current.bsiCertificationValidTo > 0) {
              <div class="flex flex-wrap items-baseline gap-x-2">
                <dt class="font-semibold text-gray-700">Zertifizierung gültig bis:</dt>
                <dd class="min-w-0 text-gray-500" data-testid="tss-bsi-certification-valid-to">
                  {{ current.bsiCertificationValidTo | UnixEpochTime }}
                  @if (bsiCertificationExpired()) {
                    <!-- amber-700 on amber-50 = 4.85:1 — the same pair the „Erstellt" state badge uses. -->
                    <span class="ml-1 inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-xs
                                 font-semibold text-amber-700"
                          data-testid="tss-bsi-certification-expired">abgelaufen</span>
                  }
                </dd>
              </div>
            }
          </dl>
        </li>

        <!-- Zeiten — nur die gesetzten Zeitpunkte, in der Reihenfolge des Lebenszyklus. -->
        <li class="rounded-xl border-[1px] border-gray-100 bg-gray-50/50 p-3">
          <div class="flex items-center gap-2">
            <span aria-hidden="true"
                  class="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-white
                         text-theme-primary-600 shadow-sm">
              <svg class="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                <path
                  d="M12 2c5.523 0 10 4.477 10 10s-4.477 10-10 10S2 17.523 2 12S6.477 2 12 2m0 2a8 8 0 1 0 0 16a8 8 0 0 0 0-16m0 2a1 1 0 0 1 .993.883L13 7v4.586l2.707 2.707a1 1 0 0 1-1.32 1.497l-.094-.083l-3-3a1 1 0 0 1-.284-.576L11 12V7a1 1 0 0 1 1-1"/>
              </svg>
            </span>
            <h3 class="text-sm font-bold tracking-tight text-gray-800">Zeiten</h3>
          </div>
          <dl class="mt-2 space-y-1 text-sm">
            @if (current.timeCreation > 0) {
              <div class="flex flex-wrap items-baseline gap-x-2">
                <dt class="font-semibold text-gray-700">Erstellt:</dt>
                <dd class="text-gray-500">{{ current.timeCreation | UnixEpochTime }}</dd>
              </div>
            }
            @if (current.timeUninit > 0) {
              <div class="flex flex-wrap items-baseline gap-x-2">
                <dt class="font-semibold text-gray-700">Vorbereitet:</dt>
                <dd class="text-gray-500">{{ current.timeUninit | UnixEpochTime }}</dd>
              </div>
            }
            @if (current.timeInit > 0) {
              <div class="flex flex-wrap items-baseline gap-x-2">
                <dt class="font-semibold text-gray-700">Initialisiert:</dt>
                <dd class="text-gray-500">{{ current.timeInit | UnixEpochTime }}</dd>
              </div>
            }
            <!-- #613: labels were swapped — `timeDefective` is "Defekt", `timeDisabled` is "Deaktiviert". -->
            @if (current.timeDefective > 0) {
              <div class="flex flex-wrap items-baseline gap-x-2">
                <dt class="font-semibold text-gray-700">Defekt:</dt>
                <dd class="text-gray-500">{{ current.timeDefective | UnixEpochTime }}</dd>
              </div>
            }
            @if (current.timeDisabled > 0) {
              <div class="flex flex-wrap items-baseline gap-x-2">
                <dt class="font-semibold text-gray-700">Deaktiviert:</dt>
                <dd class="text-gray-500">{{ current.timeDisabled | UnixEpochTime }}</dd>
              </div>
            }
          </dl>
        </li>

        <!-- Geräte / Aktive Transaktionen — zwei Auslastungszahlen, gleich aufgebaut. -->
        <li class="rounded-xl border-[1px] border-gray-100 bg-gray-50/50 p-3">
          <div class="flex items-center gap-2">
            <span aria-hidden="true"
                  class="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-white
                         text-theme-primary-600 shadow-sm">
              <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                   stroke-linecap="round" stroke-linejoin="round">
                <rect height="20" rx="2" width="16" x="4" y="2"/>
                <path d="M11 18h2"/>
              </svg>
            </span>
            <h3 class="text-sm font-bold tracking-tight text-gray-800">Geräte</h3>
          </div>
          <p class="mt-2 text-sm text-gray-500">
            <span class="text-lg font-bold text-theme-primary-700">{{ current.numberRegisteredClients }}</span>
            (max. {{ current.maxNumberRegisteredClients }})
          </p>
        </li>

        <li class="rounded-xl border-[1px] border-gray-100 bg-gray-50/50 p-3">
          <div class="flex items-center gap-2">
            <span aria-hidden="true"
                  class="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-white
                         text-theme-primary-600 shadow-sm">
              <svg class="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                <path
                  d="M14.5 3a2.5 2.5 0 1 0 0 5a2.5 2.5 0 0 0 0-5M10 5.5a4.5 4.5 0 0 1 6.5-4.032a4.5 4.5 0 1 1 0 8.064A4.5 4.5 0 0 1 10 5.5m8.25 2.488a2.5 2.5 0 1 0 0-4.975A4.48 4.48 0 0 1 19 5.5a4.48 4.48 0 0 1-.75 2.488M8.435 13.25a1.25 1.25 0 0 0-.885.364l-2.05 2.05V19.5h5.627l5.803-1.45l3.532-1.508a.555.555 0 0 0-.416-1.022l-.02.005L13.614 17H10v-2h3.125a.875.875 0 1 0 0-1.75H8.438zm7.552 1.152l3.552-.817a2.56 2.56 0 0 1 3.211 2.47a2.557 2.557 0 0 1-1.414 2.287l-.027.014l-3.74 1.595l-6.196 1.549H0v-7.25h4.086l2.052-2.052a3.25 3.25 0 0 1 2.3-.948h4.689a2.875 2.875 0 0 1 2.862 3.152M3.5 16.25H2v3.25h1.5z"/>
              </svg>
            </span>
            <h3 class="text-sm font-bold tracking-tight text-gray-800">Aktive Transaktionen</h3>
          </div>
          <p class="mt-2 text-sm text-gray-500">
            <span class="text-lg font-bold text-theme-primary-700">{{ current.numberActiveTransactions }}</span>
            (max. {{ current.maxNumberActiveTransactions }})
          </p>
        </li>

        <!-- Zähler (#769) — the two monotonic counters of the TSE itself, NOT the capacity numbers above.
             Rendered only when the device reports them: a TSE that has never signed answers an empty string,
             and „—" would read as „null signatures so far", which is a different statement. -->
        @if (current.signatureCounter || current.transactionCounter) {
          <li class="rounded-xl border-[1px] border-gray-100 bg-gray-50/50 p-3" data-testid="tss-counters">
            <div class="flex items-center gap-2">
              <span aria-hidden="true"
                    class="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-white
                           text-theme-primary-600 shadow-sm">
                <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                     stroke-linecap="round" stroke-linejoin="round">
                  <path d="M4 19V5"/>
                  <path d="M9 19V9"/>
                  <path d="M14 19v-6"/>
                  <path d="M19 19V7"/>
                </svg>
              </span>
              <h3 class="text-sm font-bold tracking-tight text-gray-800">Zähler</h3>
            </div>
            <dl class="mt-2 space-y-1 text-sm">
              @if (current.signatureCounter) {
                <div class="flex flex-wrap items-baseline gap-x-2">
                  <dt class="font-semibold text-gray-700">Signaturzähler:</dt>
                  <dd class="min-w-0 break-all font-mono text-gray-500">{{ current.signatureCounter }}</dd>
                </div>
              }
              @if (current.transactionCounter) {
                <div class="flex flex-wrap items-baseline gap-x-2">
                  <dt class="font-semibold text-gray-700">Transaktionszähler:</dt>
                  <dd class="min-w-0 break-all font-mono text-gray-500">{{ current.transactionCounter }}</dd>
                </div>
              }
            </dl>
          </li>
        }

        <li class="rounded-xl border-[1px] border-gray-100 bg-gray-50/50 p-3">
          <div class="flex items-center gap-2">
            <span aria-hidden="true"
                  class="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-white
                         text-theme-primary-600 shadow-sm">
              <svg class="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                <path
                  d="M4 4a2 2 0 0 1 2-2h6.172a2 2 0 0 1 1.414.586l5.11 5.109a3 3 0 0 0-.302.267L16.356 10H14a2 2 0 0 1-2-2V3.5H6a.5.5 0 0 0-.5.5v16a.5.5 0 0 0 .5.5h12a.5.5 0 0 0 .5-.5v-2.85l1.5-1.5V20a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2zm10 4.5h3.38L13.5 4.62V8a.5.5 0 0 0 .5.5m-.803 6.072L19.1 8.669a2.286 2.286 0 1 1 3.232 3.233l-5.902 5.902a2.7 2.7 0 0 1-1.248.707l-1.83.457A1.1 1.1 0 0 1 13 19H7.75a.75.75 0 0 1 0-1.5h4.321l.42-1.68c.118-.473.362-.904.706-1.248"/>
              </svg>
            </span>
            <h3 class="text-sm font-bold tracking-tight text-gray-800">Signatur-Algorithmus</h3>
          </div>
          <p class="mt-2 break-all font-mono text-xs text-gray-500">{{ current.signatureAlgorithm }}</p>
        </li>

        @if (current.metadata) {
          <li class="rounded-xl border-[1px] border-gray-100 bg-gray-50/50 p-3 sm:col-span-2 lg:col-span-3">
            <div class="flex items-center gap-2">
              <span aria-hidden="true"
                    class="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-white
                           text-theme-primary-600 shadow-sm">
                <svg class="h-4 w-4" viewBox="0 0 48 48" fill="none" stroke="currentColor" stroke-width="4"
                     stroke-linecap="round" stroke-linejoin="round">
                  <ellipse cx="24" cy="11" rx="20" ry="6"/>
                  <path
                    d="M10.77 15.5C6.62 16.6 4 18.208 4 20c0 3.314 8.954 6 20 6s20-2.686 20-6c0-1.792-2.619-3.4-6.77-4.5c-3.526.933-8.158 1.5-13.23 1.5c-5.072 0-9.703-.567-13.23-1.5"/>
                  <path
                    d="M10.77 24.5C6.62 25.6 4 27.208 4 29c0 3.314 8.954 6 20 6s20-2.686 20-6c0-1.792-2.619-3.4-6.77-4.5c-3.526.933-8.158 1.5-13.23 1.5c-5.072 0-9.703-.567-13.23-1.5"/>
                  <path
                    d="M10.77 33.5C6.62 34.6 4 36.208 4 38c0 3.314 8.954 6 20 6s20-2.686 20-6c0-1.792-2.619-3.4-6.77-4.5c-3.526.934-8.158 1.5-13.23 1.5c-5.072 0-9.703-.566-13.23-1.5"/>
                </svg>
              </span>
              <h3 class="text-sm font-bold tracking-tight text-gray-800">Metadata</h3>
            </div>
            <dl class="mt-2 grid grid-cols-1 gap-x-4 gap-y-1 text-sm sm:grid-cols-2 lg:grid-cols-3">
              @for (pair of Object.entries(current.metadata); track pair[0]) {
                <div class="flex flex-wrap items-baseline gap-x-2">
                  <dt class="font-semibold text-gray-700">{{ pair[0] }}:</dt>
                  <dd class="min-w-0 break-all text-gray-500">{{ pair[1] }}</dd>
                </div>
              }
            </dl>
          </li>
        }
      </ul>
    </section>

    @if (isOwner()) {
      <!-- ── Optionen ─────────────────────────────────────────────────────────────────────────────────────
           The lifecycle, as the state machine it is: exactly one forward move is ever open, every other
           button is genuinely `disabled` (announced, unfocusable) rather than merely click-proofed. -->
      <section aria-labelledby="tss-options-title"
               class="rounded-2xl border-[1px] border-gray-100 bg-white p-4 shadow-sm">
        <h2 class="text-base font-bold tracking-tight text-gray-800" id="tss-options-title">Optionen</h2>
        <p class="mt-1 text-xs text-gray-500">
          Der Lebenszyklus dieser TSE. Möglich ist jeweils nur der nächste Schritt; der aktuelle Zustand ist
          hervorgehoben.
        </p>

        <div class="mt-3 flex flex-wrap items-center gap-3" data-testid="tss-state-actions">
          <div class="isolate inline-flex w-full flex-wrap rounded-xl shadow-sm sm:w-auto sm:flex-nowrap">
            @for (state of KassenSichVStateHelper.getStates(); track state; let first = $first; let last = $last) {
              <button (click)="changeState(state)" type="button"
                      [attr.aria-current]="current.state === state ? 'true' : null"
                      [disabled]="current.state === state
                                  || !KassenSichVStateHelper.canTransitionTo(current.state, state)"
                      [class.rounded-l-xl]="first"
                      [class.rounded-r-xl]="last"
                      [class.-ml-px]="!first"
                      [class.bg-theme-primary-700]="current.state === state"
                      [class.text-white]="current.state === state"
                      [class.ring-theme-primary-700]="current.state === state"
                      [class.bg-white]="current.state !== state"
                      [class.text-gray-900]="current.state !== state"
                      [class.ring-gray-300]="current.state !== state"
                      [class.hover:bg-gray-100]="current.state !== state"
                      class="relative inline-flex flex-1 select-none items-center justify-center px-3 py-2
                             text-sm font-semibold ring-1 ring-inset transition-colors duration-200
                             focus:z-10 disabled:cursor-not-allowed sm:flex-none">
                {{ KassenSichVStateHelper.toDisplayString(state) }}
              </button>
            }
          </div>

          @if (current.state == KassenSichVState.Disabled) {
            <button (click)="openReinitializeDialog()" data-testid="tss-reinitialize" type="button"
                    class="inline-flex items-center justify-center gap-2 rounded-2xl bg-theme-primary-600
                           px-5 py-2.5 text-sm font-semibold text-white shadow-lg
                           shadow-theme-primary-100 transition-all duration-200
                           hover:bg-theme-primary-700 hover:shadow-xl active:scale-95">
              <svg aria-hidden="true" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path clip-rule="evenodd" fill-rule="evenodd"
                      d="M10 3a.75.75 0 0 1 .75.75v5.5h5.5a.75.75 0 0 1 0 1.5h-5.5v5.5a.75.75 0 0 1-1.5 0v-5.5h-5.5a.75.75 0 0 1 0-1.5h5.5v-5.5A.75.75 0 0 1 10 3Z"/>
              </svg>
              Neue TSE
            </button>
          }
        </div>
      </section>
    }

    @if (current.previous.length > 0) {
      <!-- ── Frühere TSE ────────────────────────────────────────────────────────────────────────────────
           Newest first, as the server lists them. Each row names the whole handover: what it was, when it
           was active, why it went and what replaced it. -->
      <section aria-labelledby="tss-history-title"
               class="rounded-2xl border-[1px] border-gray-100 bg-white p-4 shadow-sm">
        <h2 class="text-base font-bold tracking-tight text-gray-800" id="tss-history-title">Frühere TSE</h2>
        <ul class="mt-3 space-y-3" role="list">
          @for (retired of current.previous; track retired.id) {
            <li class="rounded-xl border-[1px] border-gray-100 bg-gray-50/50 p-3">
              <dl class="grid grid-cols-1 gap-x-4 gap-y-1 text-sm sm:grid-cols-2">
                <div class="flex flex-wrap items-baseline gap-x-2 sm:col-span-2">
                  <dt class="font-semibold text-gray-700">ID:</dt>
                  <dd class="min-w-0 break-all font-mono text-xs text-gray-500">{{ retired.id }}</dd>
                </div>
                @if (retired.serialNumber) {
                  <div class="flex flex-wrap items-baseline gap-x-2 sm:col-span-2">
                    <dt class="font-semibold text-gray-700">Seriennummer:</dt>
                    <dd class="min-w-0 break-all font-mono text-xs text-gray-500">{{ retired.serialNumber }}</dd>
                  </div>
                }
                @if (retired.activatedAt) {
                  <div class="flex flex-wrap items-baseline gap-x-2">
                    <dt class="font-semibold text-gray-700">Aktiviert:</dt>
                    <dd class="text-gray-500">{{ retired.activatedAt | date:'dd.MM.yyyy HH:mm:ss' }}</dd>
                  </div>
                }
                <div class="flex flex-wrap items-baseline gap-x-2">
                  <dt class="font-semibold text-gray-700">Zurückgezogen:</dt>
                  <dd class="text-gray-500">{{ retired.retiredAt | date:'dd.MM.yyyy HH:mm:ss' }}</dd>
                </div>
                <div class="flex flex-wrap items-baseline gap-x-2 sm:col-span-2">
                  <dt class="font-semibold text-gray-700">Grund:</dt>
                  <dd class="text-gray-500">
                    {{ RetiredTechnicalSecuritySystemReasonHelper.toDisplayString(retired.reason) }}
                  </dd>
                </div>
                <div class="flex flex-wrap items-baseline gap-x-2 sm:col-span-2">
                  <dt class="font-semibold text-gray-700">Nachfolger:</dt>
                  <dd class="min-w-0 break-all font-mono text-xs text-gray-500">{{ retired.successorId }}</dd>
                </div>
              </dl>
            </li>
          }
        </ul>
      </section>
    }
  }
</div>

@if (reinitializeDialogOpen()) {
  <app-tss-reinitialize-confirm-dialog [busy]="reinitializing()"
                                       (confirmed)="onReinitializeDialogConfirmed()"
                                       (cancelled)="onReinitializeDialogCancelled()"/>
}
